import queryClient from '@src/app/providers/queryClient';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useMutation } from '@src/shared/query';
import type { AppQueryKey } from '@src/shared/query/utils';
import { createRealtimeInfiniteListCache } from '@src/shared/realtime/infiniteListCache';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  historyContactOptionFromDto,
  historyItemFromDto,
  historyItemPayloadToDto,
} from '../../lib';
import type {
  HistoryContactOption,
  HistoryContactOptionDto,
  HistoryFilterOptions,
  HistoryItem,
  HistoryItemDto,
  HistoryItemPayload,
  HistoryListResponse,
  HistoryListResponseDto,
  HistoryScope,
} from '../../types';
import {
  clientHistoryContactOptionsUrl,
  clientHistoryFilterOptionsUrl,
  clientHistoryItemUrl,
  clientHistoryUrl,
} from './constants';
import { CLIENTS_KEY } from './keys';

const HISTORY_KEY: AppQueryKey = [...CLIENTS_KEY, 'history'];
const HISTORY_LIST_KEY: AppQueryKey = [...HISTORY_KEY, 'list'];

type HistoryListParams = {
  scope: HistoryScope;
  filterParams: Record<string, unknown>;
  ordering?: string;
  pageSize: number;
};

function historyListKey({
  scope,
  filterParams,
  ordering,
  pageSize,
}: HistoryListParams): AppQueryKey {
  return [...HISTORY_LIST_KEY, scope, filterParams, ordering ?? '', pageSize];
}

const historyListCache = createRealtimeInfiniteListCache<
  HistoryItemDto,
  HistoryListResponseDto,
  HistoryListParams
>({
  queryClient,
  queryRoot: HISTORY_LIST_KEY,
  getQueryKey: historyListKey,
});

type UseClientHistoryParams = HistoryListParams & {
  /** Entries kept in the list after a change, also when they no longer match. */
  retainedIds?: number[];
};

/** The history entries of a client or of a group, one page at a time. */
export function useClientHistory({
  retainedIds = [],
  ...params
}: UseClientHistoryParams) {
  const apiClient = useAPIClient();
  const { scope, filterParams, ordering, pageSize } = params;
  return useInfiniteQuery({
    queryKey: historyListKey(params),
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: clientHistoryUrl,
        payload: {
          ...scope,
          ...filterParams,
          page: String(pageParam),
          page_size: String(pageSize),
          ...(retainedIds.length > 0
            ? { retained_ids: retainedIds.join(',') }
            : {}),
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<HistoryListResponseDto>,
    select: (data): InfiniteData<HistoryListResponse> => {
      // A retained entry may come in two pages.
      const seen = new Set<number>();
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.flatMap((item) => {
            if (seen.has(item.id)) return [];
            seen.add(item.id);
            return [historyItemFromDto(item)];
          }),
        })),
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.meta.next ? lastPage.meta.page + 1 : undefined,
    structuralSharing: (oldData, newData) =>
      historyListCache.preserveRetainedRows(oldData, newData, retainedIds),
  });
}

/** The client and Keller contacts of the entries, for the column filters. */
export function useHistoryFilterOptions(scope: HistoryScope) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...HISTORY_KEY, 'filter-options', scope],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientHistoryFilterOptionsUrl,
        payload: scope,
        options: { signal },
      }) as Promise<HistoryFilterOptions>,
  });
}

/**
 * The positions an entry of the scope may be given: the current ones, and
 * `include` (the position of the entry being edited) also when it ended.
 */
export function useHistoryContactOptions(
  scope: HistoryScope,
  { include, enabled = true }: { include?: number; enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...HISTORY_KEY, 'contact-options', scope, include ?? null],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientHistoryContactOptionsUrl,
        payload: { ...scope, ...(include ? { include } : {}) },
        options: { signal },
      }) as Promise<HistoryContactOptionDto[]>,
    select: (data): HistoryContactOption[] =>
      data.map(historyContactOptionFromDto),
  });
}

function invalidateHistory() {
  return queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
}

/** A new history entry; the list shows it once refetched. */
export function useCreateHistoryItem() {
  return useMutation<HistoryItem, Error, HistoryItemPayload>({
    mutationKey: [...HISTORY_KEY, 'create'],
    mutateFn: async ({ apiClient, ...payload }) =>
      historyItemFromDto(
        (await apiClient.request({
          url: clientHistoryUrl,
          method: 'POST',
          payload: historyItemPayloadToDto(payload),
        })) as HistoryItemDto,
      ),
    onSuccess: () => {
      void invalidateHistory();
    },
  });
}

type UpdateHistoryItemVariables = {
  id: number;
  payload: Partial<HistoryItemPayload>;
};

/** The changed fields of an entry; it keeps its place in the lists. */
export function useUpdateHistoryItem() {
  return useMutation<HistoryItem, Error, UpdateHistoryItemVariables>({
    mutationKey: [...HISTORY_KEY, 'update'],
    mutateFn: async ({ apiClient, id, payload }) => {
      const item = (await apiClient.request({
        url: clientHistoryItemUrl(id),
        method: 'PATCH',
        payload: historyItemPayloadToDto(payload),
      })) as HistoryItemDto;
      historyListCache.updateOnePreservingPosition(item);
      return historyItemFromDto(item);
    },
    onSuccess: () => {
      void invalidateHistory();
    },
  });
}
